import { useState, useEffect, useRef } from "react";

// Demo Animation Component
function DemoAnimation() {
  const [phase, setPhase] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [cursorX, setCursorX] = useState(50);
  const [cursorY, setCursorY] = useState(50);
  const [variationIndex, setVariationIndex] = useState(0);
  
  // Three different scenarios
  const variations = [
    {
      prompt: "Add this invoice to the ledger for me",
      attachmentType: "image",
      documentLabel: "Invoice #1247",
      rows: [
        { account: "Accounts Receivable", debit: "$2,450", credit: "-" },
        { account: "Sales Revenue", debit: "-", credit: "$2,250" },
        { account: "Sales Tax Payable", debit: "-", credit: "$200" },
      ],
      total: "$2,450"
    },
    {
      prompt: "Log payroll for this period",
      attachmentType: "pdf",
      documentLabel: "Payroll Report",
      rows: [
        { account: "Payroll Expense", debit: "$8,500", credit: "-" },
        { account: "Payroll Tax Payable", debit: "-", credit: "$650" },
        { account: "Cash", debit: "-", credit: "$7,850" },
      ],
      total: "$8,500"
    },
    {
      prompt: "Record this vendor payment",
      attachmentType: "image",
      documentLabel: "Receipt #892",
      rows: [
        { account: "Accounts Payable", debit: "$680", credit: "-" },
        { account: "Cash", debit: "-", credit: "$680" },
        { account: "", debit: "", credit: "" },
      ],
      total: "$680"
    }
  ];
  
  const currentVariation = variations[variationIndex];
  const promptText = currentVariation.prompt;
  
  // Animation phases:
  // 0: Initial state, start typing
  // 1: Typing complete, show invoice number
  // 2: Ledger visible, fill fields
  // 3: Fields filled, highlight green
  // 4: Highlighted, move cursor to confirm
  // 5: Click confirm, clear ledger
  // 6: Show new chat button, move cursor
  // 7: Click new chat, reset

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    
    if (phase === 0) {
      // Typing animation
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex <= promptText.length) {
          setTypedText(promptText.slice(0, charIndex));
          charIndex++;
        } else {
          clearInterval(typeInterval);
          setTimeout(() => setPhase(1), 500);
        }
      }, 50);
      return () => clearInterval(typeInterval);
    } else if (phase === 1) {
      // Show invoice number
      timeout = setTimeout(() => setPhase(2), 800);
    } else if (phase === 2) {
      // Fill fields animation
      timeout = setTimeout(() => setPhase(3), 1500);
    } else if (phase === 3) {
      // Highlight green, move cursor to Accept button (centered bottom)
      timeout = setTimeout(() => {
        setCursorX(45);
        setCursorY(88);
        // Wait for cursor animation to complete before lighting up button
        setTimeout(() => setPhase(4), 400);
      }, 1000);
    } else if (phase === 4) {
      // Move cursor and click Accept
      timeout = setTimeout(() => setPhase(5), 800);
    } else if (phase === 5) {
      // Close ledger, move cursor to New Chat button (top right)
      timeout = setTimeout(() => {
        setCursorX(92);
        setCursorY(5);
        setPhase(6);
      }, 600);
    } else if (phase === 6) {
      // Click new chat
      timeout = setTimeout(() => setPhase(7), 800);
    } else if (phase === 7) {
      // Reset loop and cycle to next variation
      timeout = setTimeout(() => {
        setTypedText("");
        setCursorX(50);
        setCursorY(50);
        setVariationIndex((prev) => (prev + 1) % variations.length);
        setPhase(0);
      }, 500);
    }
    
    return () => clearTimeout(timeout);
  }, [phase]);

  return (
    <div className="w-full h-full bg-slate-900 rounded-xl overflow-hidden relative flex flex-col">
      {/* Fake window chrome */}
      <div className="bg-slate-800 px-3 py-1.5 flex items-center gap-2 flex-shrink-0">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
        </div>
        <div className="flex-1 text-center text-slate-400 text-xs">Maven</div>
        {/* New Chat Button */}
        <button className={`px-2 py-1 rounded text-xs transition-all ${phase === 6 ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
          + New Chat
        </button>
      </div>
      
      {/* Main content area */}
      <div className="p-3 flex-1 flex flex-col relative min-h-0">
        {/* Chat input area */}
        <div className="bg-slate-800 rounded-lg p-3 flex-shrink-0">
          <div className="text-slate-300 text-sm min-h-[1.25rem]">
            {typedText}
            {phase === 0 && <span className="animate-pulse">|</span>}
          </div>
          {/* Attachment indicator - always present, just invisible when not needed */}
          <div className="mt-1.5 h-[20px]">
            <div className={`inline-flex items-center gap-1 px-2 py-1 bg-slate-700 rounded text-xs text-slate-400 transition-opacity duration-300 ${typedText === promptText && phase >= 0 && phase < 7 ? 'opacity-100' : 'opacity-0'}`}>
              {currentVariation.attachmentType === "pdf" ? (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
              <span>[{currentVariation.attachmentType} attached]</span>
            </div>
          </div>
        </div>
        
        {/* General Ledger - Always visible */}
        <div className="mt-2 lg:flex-1 bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col min-h-0">
          {/* Ledger header */}
          <div className="bg-slate-100 px-4 py-2 rounded-t-lg border-b flex items-center justify-between flex-shrink-0">
            <span className="font-semibold text-slate-700 text-xs">General Ledger</span>
            <span className={`font-bold text-slate-700 text-xs transition-opacity duration-300 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
              {currentVariation.documentLabel}
            </span>
            {/* Balanced indicator - top right */}
            <div className={`flex items-center gap-1 text-green-600 text-xs transition-opacity duration-300 ${phase >= 3 && phase <= 5 ? 'opacity-100' : 'opacity-0'}`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Balanced</span>
            </div>
          </div>
          
          {/* Ledger content */}
          <div className="p-4 lg:flex-1 flex flex-col lg:justify-between min-h-0">
            <table className="w-full text-xs table-fixed">
              <colgroup>
                <col className="w-[50%]" />
                <col className="w-[25%]" />
                <col className="w-[25%]" />
              </colgroup>
              <thead>
                <tr className="text-slate-500 border-b">
                  <th className="text-left pb-1.5 font-medium">Account</th>
                  <th className="text-right pb-1.5 font-medium">Debit</th>
                  <th className="text-right pb-1.5 font-medium">Credit</th>
                </tr>
              </thead>
              <tbody>
                {/* Dynamic rows based on current variation */}
                {currentVariation.rows.map((row, idx) => (
                  <tr key={idx} className={`transition-all duration-500 ${idx === 0 ? '' : `delay-${idx}00`} ${phase >= 2 && phase <= 5 ? 'opacity-100' : 'opacity-0'} ${phase >= 3 && phase <= 5 ? 'bg-green-50' : ''}`}>
                    <td className="py-1.5 text-slate-700 h-[28px]">
                      {phase >= 2 && phase <= 5 && row.account ? row.account : '\u00A0'}
                    </td>
                    <td className={`py-1.5 text-right h-[28px] ${row.debit === '-' ? 'text-slate-400' : 'text-slate-700'}`}>
                      {phase >= 2 && phase <= 5 && row.debit ? row.debit : '\u00A0'}
                    </td>
                    <td className={`py-1.5 text-right h-[28px] ${row.credit === '-' ? 'text-slate-400' : 'text-slate-700'}`}>
                      {phase >= 2 && phase <= 5 && row.credit ? row.credit : '\u00A0'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={`border-t font-semibold transition-all duration-500 delay-300 ${phase >= 2 && phase <= 5 ? 'opacity-100' : 'opacity-0'}`}>
                  <td className="pt-1.5 text-slate-700 h-[28px]">Total</td>
                  <td className="pt-1.5 text-right text-slate-700 h-[28px]">{phase >= 2 && phase <= 5 ? currentVariation.total : '\u00A0'}</td>
                  <td className="pt-1.5 text-right text-slate-700 h-[28px]">{phase >= 2 && phase <= 5 ? currentVariation.total : '\u00A0'}</td>
                </tr>
              </tfoot>
            </table>
            
            {/* Bottom section with accept/reject buttons - always takes up space */}
            <div className="mt-4 min-h-[28px]">
              {/* Accept/Reject buttons */}
              <div className={`flex items-center justify-center gap-3 transition-opacity duration-300 ${phase >= 3 && phase <= 5 ? 'opacity-100' : 'opacity-0'}`}>
                <button className={`px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1.5 ${phase >= 4 && phase <= 5 ? 'bg-green-600 text-white scale-105' : 'bg-green-500/10 text-green-600 border border-green-500/30'}`}>
                  <span>Accept</span>
                  <span className="opacity-70">⌘↵</span>
                </button>
                <button className="px-3 py-1.5 rounded text-xs font-medium bg-red-500/10 text-red-600 border border-red-500/30 flex items-center gap-1.5">
                  <span>Reject</span>
                  <span className="opacity-70">⌘⌫</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Animated cursor - positioned relative to entire demo window */}
      <div 
        className="absolute pointer-events-none transition-all duration-500 ease-out z-50"
        style={{ left: `${cursorX}%`, top: `${cursorY}%` }}
      >
        <svg viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1" className="w-4 h-4 drop-shadow-lg">
          <path d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.92L6.35 2.85a.5.5 0 0 0-.85.36Z" />
        </svg>
        {/* Click effect - centered at cursor tip (top-left point) */}
        {(phase === 5 || phase === 7) && (
          <div 
            className="absolute rounded-full bg-teal-400/50 animate-click-ripple" 
            style={{
              width: '24px',
              height: '24px',
              left: '2px',
              top: '2px'
            }}
          ></div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  // Rotating languages
  const languages = ["Human", "Plain English", "Like you do"];
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
  const [_isAnimating, setIsAnimating] = useState(false);

  // Fade-in on scroll for demo video section
  const demoSectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Language rotation effect
  useEffect(() => {
    const duration = 3000; // Same delay for all languages
    
    const timeout = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentLanguageIndex((prev) => (prev + 1) % languages.length);
        setIsAnimating(false);
      }, 500);
    }, duration);

    return () => clearTimeout(timeout);
  }, [currentLanguageIndex]);

  // Intersection Observer for fade-in animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 } // Trigger when 20% of the section is visible
    );

    if (demoSectionRef.current) {
      observer.observe(demoSectionRef.current);
    }

    return () => {
      if (demoSectionRef.current) {
        observer.unobserve(demoSectionRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen w-screen bg-white overflow-auto lg:overflow-hidden relative">
      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 flex items-center gap-2 z-10">
        <img src="/logo.svg" alt="Maven Logo" className="w-8 h-8 lg:w-10 lg:h-10" />
        <span className="text-xl lg:text-2xl font-bold text-slate-800" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Maven</span>
      </div>

      {/* Hero Section Container - contains the vertical divider */}
      <div className="relative h-screen">
        {/* Desktop: Vertical divider - Centered in hero section only */}
        <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-3/4 bg-gray-300"></div>

        {/* Layout - Stacked on mobile, side-by-side on desktop */}
        <div className="h-full flex flex-col lg:flex-row items-center px-6 py-20 lg:px-24 lg:py-0 gap-0">
        {/* Content */}
        <div className="w-full lg:flex-[55] flex items-center justify-center lg:justify-end lg:pr-8">
          <div className="max-w-full">
            {/* Hero Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-tight mb-8 text-center lg:text-left">
              <div>Accounting</div>
              <div className="text-slate-600">that Speaks</div>
              <div className="text-teal-700 min-h-[1.2em] relative lg:min-w-[600px]">
                <span 
                  key={currentLanguageIndex}
                  className="inline-block animate-slide-in-from-left capitalize"
                >
                  {languages[currentLanguageIndex]}
                </span>
              </div>
            </h1>
          </div>
        </div>

        {/* Mobile: Horizontal divider */}
        <div className="block lg:hidden w-3/4 h-[1px] bg-gray-300 my-8"></div>

        {/* Demo Animation */}
        <div className="w-full lg:flex-[45] flex items-center justify-center lg:justify-start lg:pl-8">
          <div className="w-full max-w-2xl lg:max-w-none lg:w-full h-[380px] lg:h-3/4">
            <DemoAnimation />
          </div>
        </div>
        </div>
      </div>

      {/* Demo Video Section */}
      <div 
        ref={demoSectionRef}
        className="w-full h-screen bg-white px-6 lg:px-24 flex items-center"
      >
        <div className="max-w-7xl mx-auto w-full">
          {/* Section Header */}
          <div 
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900">
              Watch Maven in Action
            </h2>
          </div>

          {/* Video Container */}
          <div 
            className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
              {/* YouTube Video Embed */}
              <div className="relative aspect-video">
                <iframe 
                  src="https://www.youtube.com/embed/R59xuLoSLfA?mute=1" 
                  title="Maven Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl -z-10"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -z-10"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
